import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";

const CHAPTERS = [
  {
    num: "01",
    title: "Why banks fail artists",
    copy: "Traditional lenders see irregular royalty checks and unpredictable touring income — and they see risk. We see a catalog with a pulse. Emison Management was built to translate the language of music into the language of capital.",
  },
  {
    num: "02",
    title: "The Emison legacy",
    copy: "Born from Emison Capital's work funding businesses across every corner of the financial landscape, Emison Management brings that same discipline — the packaging, the positioning, the lender relationships — to the one industry that moves culture: music.",
  },
  {
    num: "03",
    title: "Your financial business consultant",
    copy: "Let us be your financial business consultant. We empower artists and label owners with comprehensive solutions to secure personal and business funding — crafting tailored strategies that unlock the capital you require to thrive.",
  },
];

const About = () => {
  return (
    <main data-testid="about-page" className="pt-20">
      <section className="relative py-32 lg:py-44 px-6 lg:px-12 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.14] bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1603048588665-791ca8aea617?crop=entropy&cs=srgb&fm=jpg&q=85)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-espresso/80 via-espresso/90 to-espresso" />
        <div className="relative max-w-7xl mx-auto">
          <p className="text-xs font-mono-accent uppercase tracking-[0.3em] text-gold mb-8">
            About Emison Management
          </p>
          <h1 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-cream max-w-4xl">
            <MaskedLines
              lines={[
                <>Architects of independent</>,
                <span className="italic gold-text">music wealth.</span>,
              ]}
            />
          </h1>
          <p className="mt-8 text-base sm:text-lg text-sand leading-relaxed max-w-2xl">
            Your premier partner in navigating the financial landscape of the
            music industry — bridging Wall Street capital with independent
            music growth.
          </p>
        </div>
      </section>

      <section className="py-24 lg:py-32 px-6 lg:px-12 max-w-5xl mx-auto">
        <Reveal>
          <p className="font-serif italic text-2xl sm:text-3xl text-champagne leading-relaxed max-w-3xl">
            "Where our dream is helping you achieve your financial success."
          </p>
        </Reveal>
        <div className="mt-20 space-y-20">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.num} delay={0.05 * i}>
              <div className="group relative bg-[#fffdf9] border border-[#e3d3ac] p-8 lg:p-12 shadow-[0_20px_60px_rgba(45,26,11,0.12)] transition-all duration-700 hover:-translate-y-1.5 hover:shadow-[0_35px_80px_rgba(201,162,39,0.28)]">
                <span
                  className="absolute bottom-0 left-0 right-0 h-[3px]"
                  style={{ background: "linear-gradient(90deg, #8c6d3f 0%, #e5c158 50%, #8c6d3f 100%)" }}
                />
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
                  <span
                    className="md:col-span-2 w-12 h-12 rotate-45 flex items-center justify-center shadow-[0_8px_20px_rgba(201,162,39,0.4)] transition-transform duration-700 group-hover:rotate-[135deg]"
                    style={{ background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #c9a227 100%)" }}
                  >
                    <span className="-rotate-45 font-mono-accent text-xs tracking-[0.1em] text-white transition-transform duration-700 group-hover:-rotate-[135deg]">
                      {c.num}
                    </span>
                  </span>
                  <div className="md:col-span-10">
                    <h2 className="font-serif text-2xl sm:text-3xl text-cream">{c.title}</h2>
                    <span className="mt-5 block h-px w-24 bg-gradient-to-r from-[#c9a227] to-transparent" />
                    <p className="mt-5 text-base text-sand leading-relaxed max-w-2xl">{c.copy}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-24 lg:py-32 px-6 lg:px-12 bg-espresso-deep border-y border-[rgba(212,175,55,0.12)]">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <Reveal>
              <p className="text-xs font-mono-accent uppercase tracking-[0.3em] text-gold mb-4">
                The Vault Guarantee
              </p>
              <h2 className="font-serif font-medium text-2xl sm:text-3xl lg:text-4xl tracking-tight text-cream">
                Zero equity. Zero royalty surrender.{" "}
                <span className="italic gold-text">100% yours.</span>
              </h2>
              <p className="mt-6 text-base text-sand leading-relaxed">
                Every structure we arrange is debt — not a rights grab. You
                keep your masters, your publishing, and your future. Our role
                is to make you lendable, not to own a piece of you.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.15}>
              <div className="relative border border-[#e3d3ac] bg-[#fffdf9] p-10 space-y-6 shadow-[0_20px_60px_rgba(45,26,11,0.12)]">
                <span
                  className="absolute bottom-0 left-0 right-0 h-[3px]"
                  style={{ background: "linear-gradient(90deg, #8c6d3f 0%, #e5c158 50%, #8c6d3f 100%)" }}
                />
                <span
                  className="w-10 h-10 rotate-45 flex items-center justify-center mb-2 shadow-[0_8px_20px_rgba(201,162,39,0.4)]"
                  style={{ background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #c9a227 100%)" }}
                >
                  <span className="-rotate-45 font-serif italic text-white text-sm">E</span>
                </span>
                {[
                  "Retain 100% of your rights and ownership",
                  "No personal liability traps buried in fine print",
                  "Terms negotiated around your goals — not the lender's",
                  "Clarity at every step, from application to deployment",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-4">
                    <ShieldCheck className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                    <p className="text-sm text-sand leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 px-6 lg:px-12 text-center">
        <Reveal>
          <span
            className="mx-auto w-11 h-11 rotate-45 flex items-center justify-center mb-8 shadow-[0_8px_20px_rgba(201,162,39,0.4)]"
            style={{ background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #c9a227 100%)" }}
          >
            <span className="-rotate-45 font-serif italic text-white text-base">E</span>
          </span>
          <h2 className="font-serif font-semibold text-3xl sm:text-4xl tracking-tight text-[#f3e5c8]">
            Ready to <span className="italic gold-text">unlock the vault?</span>
          </h2>
          <Link
            to="/apply"
            data-testid="about-cta-apply"
            className="sheen-btn mt-10 inline-flex items-center gap-3 text-[#4D4232] text-[11px] font-semibold font-mono-accent uppercase tracking-[0.25em] px-10 py-5 shadow-[0_10px_25px_rgba(201,162,39,0.4)] hover:scale-[1.03] transition-transform"
            style={{ background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #d4af37 100%)" }}
          >
            Begin Application <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </section>
    </main>
  );
};

export default About;
